import { PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import type { Content } from '@plone/types';
import { isInternalURL } from '@plone/volto/helpers/Url/Url';
import {
  NormalizableContent,
  transformToCard,
} from '@fhnw/helpers/DesignSystem/cardTransformers';
import { normalizeLink } from '@fhnw/helpers/DesignSystem/normalizeContent';
import type { BlockWrapperDataProps } from '../BlockWrapper/BlockWrapper';
import { BlockWrapper } from '../BlockWrapper/BlockWrapper';
import { messages } from './messages';
import { AvatarList } from '@designsystem/stories/04-organisms/AvatarList';
import { AvatarProps } from '@designsystem/stories/03-molecules/Avatar';
import { RoleProps } from '@fhnw/types/role';
import { shuffle } from '@fhnw/helpers/Array/shuffle';
import { mulberry32 } from '@fhnw/helpers/Array/seededRandom';
import {
  useShuffleSeed,
  SHUFFLE_BLOCK_ATTR,
  SHUFFLE_SEED_ATTR,
} from './hooks/useShuffleSeed';

export interface BlockDataProps extends BlockWrapperDataProps {
  lecturers?: Content[];
  randomSort?: boolean;
}

interface ViewProps {
  data: BlockDataProps;
  content: Content;
  isEdit?: boolean;
  block: string;
  onChangeBlock: (blockId: string, newData: any) => void;
}

const Factory = (props: PropsWithChildren) => <>{props.children}</>;

const View = (props: ViewProps) => {
  const intl = useIntl();
  const _content = useSelector(
    (state: { content: { data: Content } }) => state.content.data,
  );
  const content =
    props?.content && (props.content?.['@id'] || props.content?.parent)
      ? props.content
      : _content;
  const data = { ...props?.data };
  const location = useSelector(
    (state: { router: { location: { pathname: string; search: string } } }) =>
      state?.router?.location,
  );
  const type =
    content?.['@type'] ||
    (location.pathname.endsWith('/add')
      ? location.search.match(/type=([^&]+)/)?.[1]
      : '');

  let lecturers = data?.lecturers;
  if (
    (!lecturers || !lecturers.length) &&
    (type === 'DegreeProgram' || type === 'ContinuingEducation')
  ) {
    lecturers = content?.lecturer;
  }

  const items: AvatarProps[] = [];

  (lecturers || []).forEach((role) => {
    const contact = role?.['@type']?.endsWith('Contact') ? role : role?.contact;

    if (!contact) {
      return;
    }

    const item = transformToCard(
      contact as NormalizableContent,
      intl,
      'person',
      undefined,
      undefined,
      false,
      { max: '5rem' },
    );

    const roleData = role as RoleProps;
    const rawLink = roleData?.link ?? '';
    const href = rawLink
      ? normalizeLink(rawLink)
      : (item?.link as string | undefined);
    const isExternal = !!rawLink && !isInternalURL(rawLink);

    items.push({
      name: roleData?.displayName || (item?.title as string),
      image: item?.image,
      description: roleData?.description,
      href,
      target: isExternal ? '_blank' : undefined,
      className: 'avatar--truncate',
    });
  });

  // Randomize the order with a seed chosen on the server and embedded in the
  // markup, so the client reproduces the exact same order during hydration
  // (no reshuffle flicker, no hydration mismatch). In edit mode there is no SSR,
  // so the seed is generated once on mount and stays stable while editing.
  // See useShuffleSeed.
  const shuffleSeed = useShuffleSeed(props?.block, !!data?.randomSort);
  const displayItems =
    shuffleSeed != null ? shuffle(items, mulberry32(shuffleSeed)) : items;

  if (!props?.isEdit && !items.length) {
    return null;
  }

  return (
    <BlockWrapper
      isEdit={props?.isEdit}
      block={props?.block}
      onChangeBlock={props?.onChangeBlock}
      data={data}
      factory={Factory}
    >
      <AvatarList
        showall={intl.formatMessage(messages.ShowAll)}
        close={intl.formatMessage(messages.Close)}
        items={displayItems}
        {...(shuffleSeed != null
          ? {
              [SHUFFLE_BLOCK_ATTR]: props?.block,
              [SHUFFLE_SEED_ATTR]: shuffleSeed,
            }
          : {})}
      />
    </BlockWrapper>
  );
};

export default View;
